import React, { useState, useEffect, useMemo } from 'react';
import BBALoader from '../components/BBALoader';
import {
  AreaChart,
  ArrowDown,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Clock,
  DollarSign,
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { getAccounts, getAccountPayments } from '../services/firestoreService';
import { getAccountExpenses, getInternalExpenses } from '../services/accountExpensesService';
import {
  getRenewsOnDateFromAccount,
  getTermEndDateFromAccount,
} from '../utils/accountProgramDeliverables';
import { SIGNING_CHANCE_META, normalizeLeadSigningChance } from '../utils/leadSigningChance';
import {
  clientDisplayName,
  collectPaymentsForAccount,
  financeBillingSnapshot,
  formatCurrency,
  parsePaymentDate,
  resolveBillingAmounts,
} from '../utils/financeReportMetrics';
import FinanceProfitLossTab from '../components/crm/FinanceProfitLossTab';
import { PinnableCard, ReportBackdrop, useReportCardMode } from '../components/crm/reportCards';
import {
  GLASS_TONE,
  GLASS_TEXT,
  GLASS_TH,
  GLASS_ROW,
  GLASS_INSET,
  GLASS_SECONDARY_BUTTON,
  GlassPage,
  GlassPageHeader,
  GlassCard,
  GlassCardHeader,
  GlassFigure,
  GlassChip,
  GlassBadge,
  GlassAvatar,
  GlassSearch,
  GlassEmpty,
  SlidingPills,
} from '../components/glass/GlassUi';

function formatFinanceDate(value) {
  if (!value) return '—';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function formatBillingCycleLabel(cycle) {
  if (!cycle) return '—';
  const c = String(cycle).toLowerCase();
  if (c === 'monthly' || c === 'month') return 'Monthly';
  if (c === 'annual' || c === 'year') return 'Annual';
  if (c === 'quarterly' || c === 'quarter') return 'Quarterly';
  if (c === 'weekly' || c === 'week') return 'Weekly';
  return String(cycle);
}

/** Cash applied to subscription forecast bars — exclude one-off checkout products. */
function isSubscriptionForecastPayment(p) {
  const t = String(p?.type || '').toLowerCase();
  if (t === 'strategy_call') return false;
  return true;
}

function advanceBillingDate(anchor, billingCycle) {
  const c = (billingCycle || 'monthly').toLowerCase();
  const d = new Date(anchor);
  if (c === 'annual' || c === 'year') d.setFullYear(d.getFullYear() + 1);
  else if (c === 'quarterly' || c === 'quarter') d.setMonth(d.getMonth() + 3);
  else if (c === 'weekly' || c === 'week') d.setDate(d.getDate() + 7);
  else d.setMonth(d.getMonth() + 1);
  return d;
}

function normalizeFirstFutureRenewal(account) {
  const snap = financeBillingSnapshot(account);
  let cursor = snap.currentPeriodEnd
    ? new Date(snap.currentPeriodEnd)
    : getRenewsOnDateFromAccount(account);
  if (!cursor || Number.isNaN(cursor.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const cycle = snap.billingCycle || 'monthly';
  while (cursor < today) {
    cursor = advanceBillingDate(cursor, cycle);
  }
  return cursor;
}

function projectionCutoffDate(account) {
  const b = account.billing || {};
  let cutoff = null;
  const termEnd = getTermEndDateFromAccount(account);
  if (termEnd && !Number.isNaN(termEnd.getTime())) cutoff = termEnd;

  if (b.cancelAtPeriodEnd && b.currentPeriodEnd) {
    const cpe = new Date(b.currentPeriodEnd);
    if (!Number.isNaN(cpe.getTime())) {
      cutoff = cutoff ? new Date(Math.min(cutoff.getTime(), cpe.getTime())) : cpe;
    }
  }
  if (b.cancelAt && !b.cancelAtPeriodEnd) {
    const ca = new Date(b.cancelAt);
    if (!Number.isNaN(ca.getTime())) {
      cutoff = cutoff ? new Date(Math.min(cutoff.getTime(), ca.getTime())) : ca;
    }
  }
  return cutoff;
}

/** Projected renewal { date, amount } for account from first future renewal through horizon */
function projectedRenewalsThrough(account, horizonEnd, subPayments) {
  const b = account.billing || {};
  if (!b.stripeSubscriptionId) return [];
  const st = (b.status || '').toLowerCase();
  if (st === 'canceled') return [];

  const { perCycle } = resolveBillingAmounts(account, subPayments);
  if (perCycle <= 0) return [];

  let cursor = normalizeFirstFutureRenewal(account);
  if (!cursor) return [];

  const cutoff = projectionCutoffDate(account);
  const cycle = financeBillingSnapshot(account).billingCycle || 'monthly';
  const out = [];
  while (cursor <= horizonEnd) {
    if (cutoff && cursor > cutoff) break;
    out.push({ date: new Date(cursor), amount: perCycle });
    cursor = advanceBillingDate(cursor, cycle);
  }
  return out;
}

/** Week starts Sunday (local), matching typical US calendar week. */
function startOfWeekSunday(dateAtMidnight) {
  const x = new Date(
    dateAtMidnight.getFullYear(),
    dateAtMidnight.getMonth(),
    dateAtMidnight.getDate(),
  );
  const day = x.getDay();
  x.setDate(x.getDate() - day);
  x.setHours(0, 0, 0, 0);
  return x;
}

/** Current week + next 3 weeks (4 buckets). Returns weeks with labels and horizon for projection. */
function buildFourWeekBuckets() {
  const today0 = new Date();
  today0.setHours(0, 0, 0, 0);
  const w0 = startOfWeekSunday(today0);
  const weeks = [];
  let lastEnd = null;
  for (let i = 0; i < 4; i++) {
    const start = new Date(w0.getFullYear(), w0.getMonth(), w0.getDate() + i * 7);
    const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 6);
    lastEnd = end;
    const sameMonth = start.getMonth() === end.getMonth();
    const period = sameMonth
      ? `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${end.getDate()}, ${end.getFullYear()}`
      : `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
    weeks.push({
      period,
      weekStart: start,
      weekEnd: end,
      projected: 0,
      received: 0,
    });
  }
  const horizonEnd = new Date(
    lastEnd.getFullYear(),
    lastEnd.getMonth(),
    lastEnd.getDate(),
    23,
    59,
    59,
    999,
  );
  return { weeks, horizonEnd };
}

/**
 * Break a month into week rows (Sunday-start, clipped to the month) so weekly
 * received/outstanding sums add up to the month exactly.
 */
function buildMonthWeekSegments(monthOffset) {
  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth() + monthOffset, 1);
  const monthEnd = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0);
  const monthLabel = monthStart.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const segments = [];
  let cursor = new Date(monthStart);
  while (cursor <= monthEnd) {
    const start = new Date(cursor);
    const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + (6 - start.getDay()));
    if (end > monthEnd) end.setTime(monthEnd.getTime());
    const monthShort = start.toLocaleDateString('en-US', { month: 'short' });
    segments.push({
      period:
        start.getDate() === end.getDate()
          ? `${monthShort} ${start.getDate()}`
          : `${monthShort} ${start.getDate()} – ${end.getDate()}`,
      weekStart: start,
      weekEnd: end,
      days: end.getDate() - start.getDate() + 1,
      daysInMonth: monthEnd.getDate(),
      projected: 0,
      received: 0,
    });
    cursor = new Date(end.getFullYear(), end.getMonth(), end.getDate() + 1);
  }
  const horizonEnd = new Date(monthEnd.getFullYear(), monthEnd.getMonth(), monthEnd.getDate(), 23, 59, 59, 999);
  return { segments, horizonEnd, monthLabel };
}

/**
 * Projected-program financials for a lead — same amounts the Projected Program
 * card on Lead Overview shows (quarterly 10% off, annual 20% off).
 */
function leadProjectionFromProposal(lead) {
  const proposal = lead?.proposedProgram;
  if (!proposal) return null;
  const perMonth = Number(proposal.totalPricePerMonth) || 0;
  if (perMonth <= 0) return null;
  const cycle = (proposal.billingCycle || 'monthly').toLowerCase();
  const perCycle = cycle === 'quarterly' ? perMonth * 3 * 0.9 : cycle === 'annual' ? perMonth * 12 * 0.8 : perMonth;
  const mrr = cycle === 'quarterly' ? perMonth * 0.9 : cycle === 'annual' ? perMonth * 0.8 : perMonth;
  const name = (proposal.programName || 'Program').replace(/\s*Program\s*$/, '').trim() || 'Program';
  return { programName: name, cycle, perCycle, mrr };
}

/** States with no subscription MRR (churned or never completed checkout). */
const MRR_EXCLUDED_STATUSES = new Set(['canceled', 'cancelled', 'incomplete']);

const REPORT_TAB_OPTIONS = [
  { value: 'cashflow', label: 'Cash Flow' },
  { value: 'pnl', label: 'Profit & Loss' },
];

const FORECAST_OPTIONS = [
  { value: 'monthly', label: 'Monthly Forecast' },
  { value: '4week', label: '4 Week Forecast' },
  { value: '12month', label: '12-Month Forecast' },
];

/** Badge tone for a Stripe subscription status (presentation only). */
function subscriptionStatusTone(rawStatus) {
  if (rawStatus === 'active') return 'mint';
  if (rawStatus === 'trialing') return 'blue';
  if (rawStatus === 'past_due' || rawStatus === 'unpaid') return 'amber';
  if (rawStatus === 'canceled' || rawStatus === 'cancelled') return 'red';
  return 'neutral';
}

const FinanceReports = () => {
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const cardMode = useReportCardMode();

  const [loading, setLoading] = useState(true);
  const [clientAccounts, setClientAccounts] = useState([]);
  const [leadAccounts, setLeadAccounts] = useState([]);
  const [paymentsByAccountId, setPaymentsByAccountId] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const projectsPerPage = 5;
  const [forecastTab, setForecastTab] = useState('12month');
  const [forecastBarTooltip, setForecastBarTooltip] = useState(null);
  const [monthOffset, setMonthOffset] = useState(0);
  const [leadChanceFilter, setLeadChanceFilter] = useState('green');
  const [includeLeadsInForecast, setIncludeLeadsInForecast] = useState(false);
  const [expensesByAccountId, setExpensesByAccountId] = useState({});
  const [internalExpenses, setInternalExpenses] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const reportTab = searchParams.get('tab') === 'pnl' ? 'pnl' : 'cashflow';
  const setReportTab = (tab) => setSearchParams(tab === 'pnl' ? { tab: 'pnl' } : {}, { replace: true });

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const accounts = await getAccounts();
        const clients = (accounts || []).filter((a) => a.type === 'Clients');
        setClientAccounts(clients);
        setLeadAccounts((accounts || []).filter((a) => a.type === 'Leads'));

        // Every client (not only Stripe-linked ones): P&L needs historical cash from ended
        // clients and expenses from all of them. Getters already swallow per-account errors.
        const [payEntries, expenseEntries, internal] = await Promise.all([
          Promise.all(clients.map(async (a) => [a.id, (await getAccountPayments(a.id)) || []])),
          Promise.all(clients.map(async (a) => [a.id, (await getAccountExpenses(a.id)) || []])),
          getInternalExpenses(),
        ]);
        setPaymentsByAccountId(Object.fromEntries(payEntries));
        setExpensesByAccountId(Object.fromEntries(expenseEntries));
        setInternalExpenses(internal);
      } catch (e) {
        console.error('FinanceReports load:', e);
        setClientAccounts([]);
        setLeadAccounts([]);
        setPaymentsByAccountId({});
        setExpensesByAccountId({});
        setInternalExpenses([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const stripeAccounts = useMemo(
    () => (clientAccounts || []).filter((a) => a.billing?.stripeSubscriptionId),
    [clientAccounts],
  );

  const horizonEnd = useMemo(() => {
    const t = new Date();
    return new Date(t.getFullYear(), t.getMonth() + 12, 1);
  }, []);

  const pipelineData = useMemo(() => {
    const today = new Date();
    const start = new Date(today.getFullYear(), today.getMonth(), 1);
    const months = [];
    for (let i = 0; i < 12; i++) {
      const d = new Date(start.getFullYear(), start.getMonth() + i, 1);
      months.push({
        period: d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
        monthStart: d,
        projected: 0,
        received: 0,
      });
    }

    stripeAccounts.forEach((account) => {
      if ((account.billing?.status || '').toLowerCase() === 'canceled') return;
      const pay = paymentsByAccountId[account.id] || [];
      const merged = collectPaymentsForAccount(account, pay);
      const renewals = projectedRenewalsThrough(account, horizonEnd, pay);

      for (let idx = 0; idx < 12; idx++) {
        let pSum = 0;
        renewals.forEach(({ date, amount }) => {
          const i =
            (date.getFullYear() - start.getFullYear()) * 12 + (date.getMonth() - start.getMonth());
          if (i === idx) pSum += amount;
        });
        let rSum = 0;
        merged.forEach((inv) => {
          if ((inv.status || '').toLowerCase() !== 'paid') return;
          if (!isSubscriptionForecastPayment(inv)) return;
          const payDate = parsePaymentDate(inv);
          if (!payDate) return;
          const amt = Math.abs(Number(inv.amount) || 0);
          if (amt <= 0) return;
          const i =
            (payDate.getFullYear() - start.getFullYear()) * 12 + (payDate.getMonth() - start.getMonth());
          if (i === idx) rSum += amt;
        });
        months[idx].received += rSum;
        months[idx].projected += Math.max(0, pSum - rSum);
      }
    });

    return months;
  }, [stripeAccounts, horizonEnd, paymentsByAccountId]);

  const weeklyPipelineData = useMemo(() => {
    const { weeks, horizonEnd: wHorizon } = buildFourWeekBuckets();
    stripeAccounts.forEach((account) => {
      if ((account.billing?.status || '').toLowerCase() === 'canceled') return;
      const pay = paymentsByAccountId[account.id] || [];
      const merged = collectPaymentsForAccount(account, pay);
      const renewals = projectedRenewalsThrough(account, wHorizon, pay);

      weeks.forEach((w) => {
        const ws = new Date(w.weekStart.getFullYear(), w.weekStart.getMonth(), w.weekStart.getDate());
        const we = new Date(w.weekEnd.getFullYear(), w.weekEnd.getMonth(), w.weekEnd.getDate());

        let pSum = 0;
        renewals.forEach(({ date, amount }) => {
          const renewalDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
          if (renewalDay >= ws && renewalDay <= we) pSum += amount;
        });

        let rSum = 0;
        merged.forEach((inv) => {
          if ((inv.status || '').toLowerCase() !== 'paid') return;
          if (!isSubscriptionForecastPayment(inv)) return;
          const payDate = parsePaymentDate(inv);
          if (!payDate) return;
          const amt = Math.abs(Number(inv.amount) || 0);
          if (amt <= 0) return;
          const day = new Date(payDate.getFullYear(), payDate.getMonth(), payDate.getDate());
          if (day >= ws && day <= we) rSum += amt;
        });

        w.received += rSum;
        w.projected += Math.max(0, pSum - rSum);
      });
    });

    return weeks;
  }, [stripeAccounts, paymentsByAccountId]);

  /** Selected month split into its weeks — received + outstanding per week. */
  const monthlyForecast = useMemo(() => {
    const { segments, horizonEnd: mHorizon, monthLabel } = buildMonthWeekSegments(monthOffset);
    stripeAccounts.forEach((account) => {
      if ((account.billing?.status || '').toLowerCase() === 'canceled') return;
      const pay = paymentsByAccountId[account.id] || [];
      const merged = collectPaymentsForAccount(account, pay);
      const renewals = projectedRenewalsThrough(account, mHorizon, pay);

      segments.forEach((w) => {
        let pSum = 0;
        renewals.forEach(({ date, amount }) => {
          const renewalDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
          if (renewalDay >= w.weekStart && renewalDay <= w.weekEnd) pSum += amount;
        });

        let rSum = 0;
        merged.forEach((inv) => {
          if ((inv.status || '').toLowerCase() !== 'paid') return;
          if (!isSubscriptionForecastPayment(inv)) return;
          const payDate = parsePaymentDate(inv);
          if (!payDate) return;
          const amt = Math.abs(Number(inv.amount) || 0);
          if (amt <= 0) return;
          const day = new Date(payDate.getFullYear(), payDate.getMonth(), payDate.getDate());
          if (day >= w.weekStart && day <= w.weekEnd) rSum += amt;
        });

        w.received += rSum;
        w.projected += Math.max(0, pSum - rSum);
      });
    });
    return { segments, monthLabel };
  }, [stripeAccounts, paymentsByAccountId, monthOffset]);

  /** Leads with a Projected Program, filtered by signing chance (green, or green + yellow). */
  const projectedLeadRows = useMemo(() => {
    const allowed = leadChanceFilter === 'greenYellow' ? ['green', 'yellow'] : ['green'];
    return (leadAccounts || [])
      .map((lead) => {
        const projection = leadProjectionFromProposal(lead);
        if (!projection) return null;
        const chance = normalizeLeadSigningChance(lead);
        if (!allowed.includes(chance)) return null;
        return { leadId: lead.id, leadName: clientDisplayName(lead), chance, ...projection };
      })
      .filter(Boolean)
      .sort((a, b) => b.mrr - a.mrr);
  }, [leadAccounts, leadChanceFilter]);

  const leadsProjectedMrr = useMemo(
    () => projectedLeadRows.reduce((s, r) => s + r.mrr, 0),
    [projectedLeadRows],
  );

  const cashFlowMetrics = useMemo(() => {
    const today0 = new Date();
    today0.setHours(0, 0, 0, 0);
    const next30End = new Date(today0);
    next30End.setDate(next30End.getDate() + 30);
    const next90End = new Date(today0);
    next90End.setDate(next90End.getDate() + 90);
    const monthStart = new Date(today0.getFullYear(), today0.getMonth(), 1);
    const monthEnd = new Date(today0.getFullYear(), today0.getMonth() + 1, 0, 23, 59, 59);

    let receivedThisMonth = 0;
    stripeAccounts.forEach((a) => {
      const merged = collectPaymentsForAccount(a, paymentsByAccountId[a.id] || []);
      merged.forEach((p) => {
        const d = parsePaymentDate(p);
        if (!d || d < monthStart || d > monthEnd) return;
        if ((p.status || '').toLowerCase() !== 'paid') return;
        receivedThisMonth += Number(p.amount) || 0;
      });
    });

    let next30Sum = 0;
    let next90Sum = 0;
    let pipeline12 = 0;
    stripeAccounts.forEach((account) => {
      if ((account.billing?.status || '').toLowerCase() === 'canceled') return;
      const renewals = projectedRenewalsThrough(account, horizonEnd, paymentsByAccountId[account.id]);
      renewals.forEach(({ date, amount }) => {
        const renewalDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        if (renewalDay <= today0) return;
        if (renewalDay <= next30End) next30Sum += amount;
        if (renewalDay <= next90End) next90Sum += amount;
        pipeline12 += amount;
      });
    });

    return {
      receivedThisMonth,
      next30Sum,
      next90Sum,
      pipeline12,
    };
  }, [stripeAccounts, paymentsByAccountId, horizonEnd]);

  const projectRows = useMemo(() => {
    return stripeAccounts.map((account) => {
      const b = account.billing || {};
      const snap = financeBillingSnapshot(account);
      const subPay = paymentsByAccountId[account.id];
      const { perCycle, mrr } = resolveBillingAmounts(account, subPay);
      const renewals = projectedRenewalsThrough(account, horizonEnd, subPay).slice(0, 6);
      const plan = (b.planKey || 'subscription').replace(/_/g, ' ');
      const termEnd = getTermEndDateFromAccount(account);
      const renewsOn = getRenewsOnDateFromAccount(account);
      const startDate = account.program?.startDate || b.currentPeriodStart || null;
      return {
        accountId: account.id,
        projectLabel: plan,
        clientName: clientDisplayName(account),
        perCycle,
        mrr,
        billingCycleLabel: formatBillingCycleLabel(snap.billingCycle),
        startDate,
        termEndDate: termEnd ? termEnd.toISOString() : null,
        renewsOnDate: renewsOn ? renewsOn.toISOString() : null,
        renewals,
        status: (b.status || '—').replace(/_/g, ' '),
        rawStatus: (b.status || '').toLowerCase().trim(),
      };
    });
  }, [stripeAccounts, horizonEnd, paymentsByAccountId]);

  const filteredProjects = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return projectRows;
    return projectRows.filter(
      (r) =>
        r.clientName.toLowerCase().includes(q) || r.projectLabel.toLowerCase().includes(q),
    );
  }, [projectRows, searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  useEffect(() => {
    setForecastBarTooltip(null);
  }, [forecastTab, monthOffset, includeLeadsInForecast, leadChanceFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredProjects.length / projectsPerPage));
  const indexOfLast = currentPage * projectsPerPage;
  const indexOfFirst = indexOfLast - projectsPerPage;
  const currentProjects = filteredProjects.slice(indexOfFirst, indexOfLast);

  /** Match the client table: sum Monthly for every subscriber except canceled / incomplete, including past_due & unpaid. */
  const stripeMrr = useMemo(
    () =>
      projectRows
        .filter((r) => !MRR_EXCLUDED_STATUSES.has(r.rawStatus))
        .reduce((s, r) => s + r.mrr, 0),
    [projectRows],
  );

  if (loading) {
    return (
      <GlassPage backdrop={<ReportBackdrop />}>
        <div className="flex h-64 items-center justify-center"><BBALoader size={96} /></div>
      </GlassPage>
    );
  }

  const baseForecastData =
    forecastTab === '4week'
      ? weeklyPipelineData
      : forecastTab === 'monthly'
        ? monthlyForecast.segments
        : pipelineData;

  // Layer projected lead revenue onto each bucket when toggled on (never onto past months).
  const activeForecastData = baseForecastData.map((d) => {
    let leads = 0;
    if (includeLeadsInForecast && leadsProjectedMrr > 0) {
      if (forecastTab === '12month') leads = leadsProjectedMrr;
      else if (forecastTab === '4week') leads = (leadsProjectedMrr * 12) / 52;
      else if (monthOffset >= 0) leads = leadsProjectedMrr * (d.days / d.daysInMonth);
    }
    return { ...d, leads };
  });
  const maxForecastBar = Math.max(
    ...activeForecastData.map((d) => d.projected + d.received + d.leads),
    1,
  );
  const forecastTotals = activeForecastData.reduce(
    (acc, d) => {
      acc.received += d.received;
      acc.projected += d.projected;
      acc.leads += d.leads;
      return acc;
    },
    { received: 0, projected: 0, leads: 0 },
  );
  const forecastGrandTotal = forecastTotals.received + forecastTotals.projected + forecastTotals.leads;

  // Cash Flow Summary figures sit in the header on the cash flow tab
  const stats = reportTab === 'cashflow' ? [
    { icon: ArrowDown, value: formatCurrency(cashFlowMetrics.receivedThisMonth), label: 'Received this month', valueClassName: GLASS_TEXT.mint },
    { icon: DollarSign, value: formatCurrency(stripeMrr), label: 'MRR', valueClassName: GLASS_TEXT.mint },
    { icon: CalendarDays, value: formatCurrency(cashFlowMetrics.next30Sum), label: 'Next 30 days' },
    { icon: Clock, value: formatCurrency(cashFlowMetrics.next90Sum), label: 'Next 90 days' },
    { icon: AreaChart, value: formatCurrency(cashFlowMetrics.pipeline12), label: 'Outstanding pipeline' },
  ] : [];

  const forecastTitle = (FORECAST_OPTIONS.find((o) => o.value === forecastTab) || FORECAST_OPTIONS[2]).label;
  const muted = 'text-gray-500 dark:text-white/[0.45]';
  const monthNavBtn = 'grid h-9 w-9 place-items-center rounded-full border border-black/10 bg-white/40 text-gray-700 transition hover:border-[#F80707]/50 hover:text-[#F80707] dark:border-white/[0.15] dark:bg-white/[0.04] dark:text-white/70 dark:hover:border-[#F80707]/60 dark:hover:text-[#FF4D40]';
  const pageBtn = (disabled) => `h-9 rounded-full px-4 text-sm font-medium transition ${
    disabled
      ? 'cursor-not-allowed border border-black/[0.05] text-gray-400 dark:border-white/[0.05] dark:text-white/20'
      : 'border border-black/10 text-gray-700 hover:bg-black/[0.04] dark:border-white/10 dark:text-white/70 dark:hover:bg-white/[0.06]'
  }`;

  return (
    <GlassPage backdrop={<ReportBackdrop />}>
      <GlassPageHeader
        onBack={() => navigate('/crm/reports')}
        backTitle="Back to reports"
        center={<SlidingPills options={REPORT_TAB_OPTIONS} value={reportTab} onChange={setReportTab} ariaLabel="Finance report" />}
        title={<>Finance<br />Reports</>}
        stats={stats}
        statsKey={reportTab}
      />

      {/* My view (card mode) renders both tabs so a card pinned from either shows up */}
      {(cardMode || reportTab === 'pnl') && (
        <FinanceProfitLossTab
          clientAccounts={clientAccounts}
          paymentsByAccountId={paymentsByAccountId}
          expensesByAccountId={expensesByAccountId}
          internalExpenses={internalExpenses}
          darkMode={darkMode}
        />
      )}
      {(cardMode || reportTab === 'cashflow') && (
      <div className="space-y-4">

      <PinnableCard id="forecast" title="Cash Flow Forecast" size="wide">
      <GlassCard className="p-5" delay={0}>
        <GlassCardHeader
          title={forecastTitle}
          right={<SlidingPills size="sm" options={FORECAST_OPTIONS} value={forecastTab} onChange={setForecastTab} ariaLabel="Forecast range" />}
          className="flex-wrap"
        />
        {forecastTab === 'monthly' && (
          <div className="mt-4 flex items-center gap-3">
            <button
              type="button"
              aria-label="Previous month"
              onClick={() => setMonthOffset((m) => m - 1)}
              className={monthNavBtn}
            >
              <ChevronLeft className="h-4 w-4" strokeWidth={1.75} />
            </button>
            <span className="min-w-[140px] text-center text-sm font-semibold text-gray-900 dark:text-white">
              {monthlyForecast.monthLabel}
            </span>
            <button
              type="button"
              aria-label="Next month"
              onClick={() => setMonthOffset((m) => m + 1)}
              className={monthNavBtn}
            >
              <ChevronRight className="h-4 w-4" strokeWidth={1.75} />
            </button>
            {monthOffset !== 0 && (
              <button
                type="button"
                onClick={() => setMonthOffset(0)}
                className="text-xs font-medium text-[#F80707] hover:underline dark:text-[#FF4D40]"
              >
                This month
              </button>
            )}
          </div>
        )}
        {forecastGrandTotal > 0 && (
          <>
            <GlassFigure
              className="mt-4"
              value={formatCurrency(forecastGrandTotal)}
              caption={`Total ${
                forecastTab === 'monthly'
                  ? `· ${monthlyForecast.monthLabel}`
                  : forecastTab === '4week'
                    ? '· next 4 weeks'
                    : '· next 12 months'
              }`}
            />
            <div className="mt-4 flex flex-wrap gap-2">
              <GlassChip color={GLASS_TONE.mint} label="Received" value={formatCurrency(forecastTotals.received)} />
              <GlassChip color={GLASS_TONE.red} label="Outstanding" value={formatCurrency(forecastTotals.projected)} />
              {forecastTotals.leads > 0 && (
                <GlassChip color={GLASS_TONE.amber} label="Projected sales" value={formatCurrency(forecastTotals.leads)} />
              )}
            </div>
          </>
        )}
        {activeForecastData.some((row) => row.projected > 0 || row.received > 0 || row.leads > 0) ? (
          <div className="relative mt-4">
            <div className="flex h-56 items-end justify-between gap-2 pt-8">
              {activeForecastData.map((data, index) => {
                const chartHeight = 168;
                const leadsAmt = data.leads || 0;
                const total = data.projected + data.received + leadsAmt;
                const totalHeight =
                  maxForecastBar > 0 ? (total / maxForecastBar) * chartHeight : 0;
                const minBarHeight = 20;
                const stackHeight = Math.max(totalHeight, total > 0 ? minBarHeight : 0);
                const segHeight = (value) => (total > 0 ? (value / total) * stackHeight : 0);
                const hRec = segHeight(data.received);
                const hProj = segHeight(data.projected);
                const hLeads = segHeight(leadsAmt);
                return (
                  <div
                    key={index}
                    className="flex min-w-0 flex-1 flex-col items-center"
                    onMouseLeave={() => setForecastBarTooltip(null)}
                  >
                    <div className="relative flex w-full flex-col justify-end" style={{ height: `${chartHeight}px` }}>
                      <div aria-hidden className="absolute inset-0 rounded-xl bg-black/[0.035] dark:bg-white/[0.035]" />
                      {forecastBarTooltip?.index === index && (
                        <div
                          role="tooltip"
                          className="pointer-events-none absolute left-1/2 top-1 z-20 -translate-x-1/2 whitespace-nowrap rounded-2xl border border-black/[0.06] bg-white/[0.85] px-3 py-1.5 text-xs font-semibold text-gray-900 shadow-lg backdrop-blur-xl dark:border-white/10 dark:bg-[#14110f]/90 dark:text-white"
                        >
                          {forecastBarTooltip.kind === 'received'
                            ? 'Received'
                            : forecastBarTooltip.kind === 'leads'
                              ? 'Projected sales'
                              : 'Outstanding'}
                          : {formatCurrency(forecastBarTooltip.amount)}
                        </div>
                      )}
                      <div
                        className={`absolute -top-10 left-1/2 -translate-x-1/2 whitespace-nowrap text-xs font-medium tabular-nums ${muted}`}
                      >
                        {formatCurrency(total)}
                      </div>
                      {total === 0 ? (
                        <div
                          className="relative w-full rounded-xl bg-black/[0.06] transition-all duration-300 dark:bg-white/[0.08]"
                          style={{ height: `${minBarHeight}px` }}
                        />
                      ) : (
                        <div
                          className="relative flex w-full flex-col justify-end overflow-hidden rounded-xl transition-all duration-300"
                          style={{ height: `${stackHeight}px`, minHeight: minBarHeight }}
                        >
                          {leadsAmt > 0 && (
                            <div
                              className="w-full cursor-default"
                              style={{ height: `${hLeads}px`, backgroundColor: GLASS_TONE.amber }}
                              onMouseEnter={() =>
                                setForecastBarTooltip({
                                  index,
                                  kind: 'leads',
                                  amount: leadsAmt,
                                })
                              }
                            />
                          )}
                          {data.received > 0 && (
                            <div
                              className="w-full cursor-default"
                              style={{ height: `${hRec}px`, backgroundColor: GLASS_TONE.mint }}
                              onMouseEnter={() =>
                                setForecastBarTooltip({
                                  index,
                                  kind: 'received',
                                  amount: data.received,
                                })
                              }
                            />
                          )}
                          {data.projected > 0 && (
                            <div
                              className="w-full cursor-default"
                              style={{ height: `${hProj}px`, backgroundColor: GLASS_TONE.red }}
                              onMouseEnter={() =>
                                setForecastBarTooltip({
                                  index,
                                  kind: 'outstanding',
                                  amount: data.projected,
                                })
                              }
                            />
                          )}
                        </div>
                      )}
                    </div>
                    <div
                      className={`mt-2 break-words px-0.5 text-center text-xs leading-tight ${muted}`}
                    >
                      {data.period}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-4 flex flex-wrap justify-center gap-2">
              <GlassChip color={GLASS_TONE.mint} label="Received (paid invoices)" />
              <GlassChip color={GLASS_TONE.red} label="Outstanding renewals (net of same-period paid invoices per client)" />
              {includeLeadsInForecast && (
                <GlassChip
                  color={GLASS_TONE.amber}
                  label={`Projected sales (${leadChanceFilter === 'greenYellow' ? 'green + yellow' : 'green'} leads)`}
                />
              )}
            </div>
          </div>
        ) : (
          <GlassEmpty className="h-56 text-base">
            {forecastTab === '4week'
              ? 'No payments or projected renewals in the next four weeks (sync Stripe on client accounts).'
              : forecastTab === 'monthly'
                ? `No payments or projected renewals in ${monthlyForecast.monthLabel}.`
                : 'No payments or projected renewals in the next twelve months (sync Stripe on client accounts).'}
          </GlassEmpty>
        )}
      </GlassCard>
      </PinnableCard>

      <PinnableCard id="projected-sales" title="Projected Sales" size="wide">
      <GlassCard className="p-5" delay={80}>
        <GlassCardHeader title="Projected Sales" />
        <p className={`mt-1 text-xs ${muted}`}>
          Leads with a Projected Program, filtered by signing chance. Toggle them into the
          forecast above to see cash flow if they sign.
        </p>

        <div className="mb-4 mt-4 flex flex-wrap items-center gap-3">
          <SlidingPills
            size="sm"
            ariaLabel="Signing chance filter"
            value={leadChanceFilter}
            onChange={setLeadChanceFilter}
            options={[
              { value: 'green', label: 'Green only', dots: ['green'] },
              { value: 'greenYellow', label: 'Green + Yellow', dots: ['green', 'yellow'] },
            ].map((opt) => ({
              value: opt.value,
              label: (
                <span className="inline-flex items-center gap-2">
                  <span className="inline-flex items-center gap-1">
                    {opt.dots.map((c) => (
                      <span
                        key={c}
                        className="inline-block h-2.5 w-2.5 rounded-full"
                        style={{ backgroundColor: SIGNING_CHANCE_META[c].dotColor }}
                      />
                    ))}
                  </span>
                  {opt.label}
                </span>
              ),
            }))}
          />

          <button
            type="button"
            role="switch"
            aria-checked={includeLeadsInForecast}
            onClick={() => setIncludeLeadsInForecast((v) => !v)}
            className={includeLeadsInForecast
              ? 'inline-flex items-center gap-2 rounded-full bg-[#F80707] px-4 py-2 text-sm font-semibold text-white shadow-[0_8px_24px_-10px_rgba(248,7,7,0.75)] transition hover:bg-[#C90505]'
              : GLASS_SECONDARY_BUTTON}
          >
            {includeLeadsInForecast ? '✓ Included in forecast above' : 'Include in forecast above'}
          </button>
        </div>

        <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-3">
          <div className={`${GLASS_INSET} px-4 py-3`}>
            <div className={`text-[11px] ${muted}`}>Projected MRR</div>
            <div className={`mt-1 text-2xl font-semibold tabular-nums ${GLASS_TEXT.amber}`}>
              {formatCurrency(leadsProjectedMrr)}
            </div>
          </div>
          <div className={`${GLASS_INSET} px-4 py-3`}>
            <div className={`text-[11px] ${muted}`}>Projected / year</div>
            <div className={`mt-1 text-2xl font-semibold tabular-nums ${GLASS_TEXT.amber}`}>
              {formatCurrency(leadsProjectedMrr * 12)}
            </div>
          </div>
          <div className={`${GLASS_INSET} px-4 py-3`}>
            <div className={`text-[11px] ${muted}`}>Leads included</div>
            <div className={`mt-1 text-2xl font-semibold tabular-nums ${GLASS_TEXT.strong}`}>
              {projectedLeadRows.length}
            </div>
          </div>
        </div>

        {projectedLeadRows.length === 0 ? (
          <GlassEmpty className="py-8">
            No {leadChanceFilter === 'greenYellow' ? 'green or yellow' : 'green'} leads with a
            Projected Program yet. Set a proposed program on a lead to see it here.
          </GlassEmpty>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[600px] text-sm">
              <thead>
                <tr>
                  {['Lead', 'Signing chance', 'Program', 'Per cycle', 'Monthly'].map((h) => (
                    <th key={h} className={`${GLASS_TH} whitespace-nowrap`}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="text-gray-700 dark:text-white/75">
                {projectedLeadRows.map((row) => (
                  <tr
                    key={row.leadId}
                    className={`${GLASS_ROW} cursor-pointer`}
                    onClick={() => navigate(`/crm/lead/${row.leadId}`)}
                  >
                    <td className="whitespace-nowrap py-3 pr-4">
                      <div className="flex items-center gap-3">
                        <GlassAvatar name={row.leadName} />
                        <span className="font-medium text-gray-900 dark:text-white">{row.leadName}</span>
                      </div>
                    </td>
                    <td className="whitespace-nowrap py-3 pr-4">
                      <span className="inline-flex items-center gap-2">
                        <span
                          className="inline-block h-2.5 w-2.5 rounded-full"
                          style={{ backgroundColor: SIGNING_CHANCE_META[row.chance].dotColor }}
                        />
                        <span>{SIGNING_CHANCE_META[row.chance].label}</span>
                      </span>
                    </td>
                    <td className="whitespace-nowrap py-3 pr-4">
                      {row.programName}
                    </td>
                    <td className="whitespace-nowrap py-3 pr-4 tabular-nums">
                      {formatCurrency(row.perCycle)}
                      <span className={`ml-1 text-xs ${muted}`}>
                        / {row.cycle === 'annual' ? 'year' : row.cycle === 'quarterly' ? 'quarter' : 'month'}
                      </span>
                    </td>
                    <td className={`whitespace-nowrap py-3 font-semibold tabular-nums ${GLASS_TEXT.amber}`}>
                      {formatCurrency(row.mrr)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </GlassCard>
      </PinnableCard>

      <PinnableCard id="clients" title="Clients" size="wide">
      <GlassCard className="p-5" delay={140}>
        <GlassCardHeader title="Clients" />
        <p className={`mt-1 text-xs ${muted}`}>
          Stripe subscriptions from your client accounts. Open a row for billing details or go to{' '}
          <button
            type="button"
            onClick={() => navigate('/crm/clients')}
            className="font-medium text-[#F80707] hover:underline dark:text-[#FF4D40]"
          >
            Accounts
          </button>
          .
        </p>

        <GlassSearch
          className="mb-4 mt-4"
          placeholder="Search clients or plan…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-sm">
            <thead>
              <tr>
                <th className={`${GLASS_TH} whitespace-nowrap`}>Client</th>
                <th className={`${GLASS_TH} whitespace-nowrap`}>Monthly</th>
                <th className={`${GLASS_TH} whitespace-nowrap`}>Cycle</th>
                <th className={`${GLASS_TH} whitespace-nowrap`}>Start</th>
                <th className={`${GLASS_TH} whitespace-nowrap`}>Minimum term</th>
                <th className={`${GLASS_TH} whitespace-nowrap`}>Renews on</th>
                <th className={`${GLASS_TH} whitespace-nowrap`}>Status</th>
                <th className={`${GLASS_TH} min-w-[200px]`}>Upcoming renewals</th>
              </tr>
            </thead>
            <tbody className="text-gray-700 dark:text-white/75">
              {currentProjects.map((row) => (
                <tr
                  key={row.accountId}
                  className={`${GLASS_ROW} cursor-pointer`}
                  onClick={() => navigate(`/crm/client/${row.accountId}`)}
                >
                  <td className="whitespace-nowrap py-3 pr-4">
                    <div className="flex items-center gap-3">
                      <GlassAvatar name={row.clientName} />
                      <span className="font-medium text-gray-900 dark:text-white">{row.clientName}</span>
                    </div>
                  </td>
                  <td className={`whitespace-nowrap py-3 pr-4 font-semibold tabular-nums ${GLASS_TEXT.mint}`}>
                    {formatCurrency(row.mrr)}
                    <span className={`block text-xs font-normal ${muted}`}>
                      {formatCurrency(row.perCycle)} / cycle
                    </span>
                  </td>
                  <td className="whitespace-nowrap py-3 pr-4">
                    {row.billingCycleLabel}
                  </td>
                  <td className="whitespace-nowrap py-3 pr-4">
                    {formatFinanceDate(row.startDate)}
                  </td>
                  <td className="whitespace-nowrap py-3 pr-4">
                    {formatFinanceDate(row.termEndDate)}
                  </td>
                  <td className="whitespace-nowrap py-3 pr-4">
                    {formatFinanceDate(row.renewsOnDate)}
                  </td>
                  <td className="whitespace-nowrap py-3 pr-4">
                    <GlassBadge tone={subscriptionStatusTone(row.rawStatus)}>
                      <span className="capitalize">{row.status}</span>
                    </GlassBadge>
                  </td>
                  <td className="py-3">
                    {row.renewals.length === 0 ? (
                      <span className={`text-xs italic ${muted}`}>None in 12 mo window</span>
                    ) : (
                      <div className="space-y-1 text-xs">
                        {row.renewals.map((u, i) => (
                          <div key={i} className="flex items-center justify-between gap-2">
                            <span>{formatFinanceDate(u.date)}</span>
                            <span className="shrink-0 font-medium tabular-nums text-gray-900 dark:text-white">{formatCurrency(u.amount)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {currentProjects.length === 0 && (
                <tr>
                  <td
                    colSpan={8}
                    className={`py-8 text-center text-sm ${muted}`}
                  >
                    {searchTerm
                      ? 'No projects match your search'
                      : 'No Stripe subscriptions yet. Link checkout or sync billing on a client.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="mt-4 flex items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className={pageBtn(currentPage === 1)}
            >
              Previous
            </button>
            <span className="text-sm font-medium tabular-nums text-gray-700 dark:text-white/70">
              Page {currentPage} of {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className={pageBtn(currentPage === totalPages)}
            >
              Next
            </button>
          </div>
        )}
      </GlassCard>
      </PinnableCard>
      </div>
      )}
    </GlassPage>
  );
};

export default FinanceReports;
